import { useState, useEffect, useRef } from "react";
import { Lightbulb, Copy, Check, Download, RefreshCw } from "lucide-react";
import { FlippableCard } from "../flippable-card";
import { useChatStream } from "@/src/pages/study/hooks/useChatStream";
import { useNotebookContext } from "@/src/pages/study/hooks/useNotebook";
import { TooltipIconButton } from "@/components/assistant-ui/tooltip-icon-button";
import { TRIAL_GATE_TOOL } from "@/src/pages/study/hooks/useChatRuntime";
import { TrialGateToolResult } from "../trial-gate-tool";
import { TrialGate } from "@/src/pages/trial-gate";
import { useCommunication } from "@/lib/CommunicationWrapper";
import { useAuth } from "@/lib/auth";
import { MarkdownRendering } from "../markdown-renderer";

export interface FlashcardData {
  term: string;
  definition: string;
  id?: string;
}

interface FlashcardProps {
  card: FlashcardData;
}

const ANSWER_MAX_HEIGHT = 250;
const EXPLANATION_MAX_HEIGHT = 230;

export const Flashcard: React.FC<FlashcardProps> = ({ card }) => {
  const frontRef = useRef<HTMLDivElement>(null);
  const backRef = useRef<HTMLDivElement>(null);
  const [frontFontSize, setFrontFontSize] = useState(30); // text-3xl
  const [backFontSize, setBackFontSize] = useState(30);
  const [showExplanation, setShowExplanation] = useState(false);
  const [savedExplanation, setSavedExplanation] = useState<string>("");
  const [isCopied, setIsCopied] = useState(false);

  // AI Explanation
  const { notebook, selectedSources } = useNotebookContext();
  const { loadSharedComponent } = useCommunication();
  const { planType } = useAuth();
  const { sendMessage, messages, isStreaming } = useChatStream(
    notebook.id.toString(),
    selectedSources
  );

  useEffect(() => {
    const adjustContent = (
      element: HTMLDivElement | null,
      setFontSize: (size: number) => void
    ) => {
      if (!element) return;

      const maxHeight = ANSWER_MAX_HEIGHT;

      // Start at text-3xl (30px)
      let fontSize = 30;
      element.style.fontSize = `${fontSize}px`;
      element.style.overflow = "hidden";
      element.style.maxHeight = "none";

      // If content overflows, shrink to text-2xl (24px)
      if (element.scrollHeight > maxHeight) {
        fontSize = 24;
        element.style.fontSize = `${fontSize}px`;
      }

      // If still overflows at text-2xl, enable scrolling
      if (element.scrollHeight > maxHeight) {
        element.style.overflow = "auto";
        element.style.maxHeight = `${maxHeight}px`;
      }

      setFontSize(fontSize);
    };

    // Small delay to ensure DOM is ready after flip animation
    const timer = setTimeout(() => {
      adjustContent(frontRef.current, setFrontFontSize);
      // Only apply adaptive sizing to answer, not explanation
      if (!showExplanation) {
        adjustContent(backRef.current, setBackFontSize);
      }
    }, 50);

    return () => clearTimeout(timer);
  }, [card.term, card.definition, showExplanation]);

  // Save explanation when it's generated
  // Check for even message count because messages alternate: user message (odd), AI response (even)
  useEffect(() => {
    if (messages.length % 2 == 0 && showExplanation) {
      const latestMessage = messages[messages.length - 1]?.content;
      if (latestMessage) {
        if (
          latestMessage.slice(0, TRIAL_GATE_TOOL.length - 1) ===
          TRIAL_GATE_TOOL.slice(1)
        ) {
          loadSharedComponent("TrialClaimGate", {
            highlight: "study_page_paywall_type",
          });
          setShowExplanation(false);
        } else setSavedExplanation(latestMessage);
      }
    }
  }, [messages, showExplanation]);

  const generateExplanationPrompt = () => {
    return `Please explain this flashcard:
            Question: ${card.term}
            Answer: ${card.definition}

            Provide a concise explanation of the concept.`;
  };

  const handleExplain = async (e) => {
    // If we already have an explanation, just show it
    e.stopPropagation();
    if (planType === "pro") {
      setShowExplanation(true);
      if (savedExplanation) {
        return;
      }
      await sendMessage(generateExplanationPrompt());
    } else
      loadSharedComponent("TrialClaimGate", {
        highlight: "study_page_paywall_type",
      });
  };

  const handleBackToAnswer = (e) => {
    e.stopPropagation();
    setShowExplanation(false);
  };

  const handleCopy = async (e) => {
    e.stopPropagation();
    if (!savedExplanation || isCopied) return;

    try {
      await navigator.clipboard.writeText(savedExplanation);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 3000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleExportMarkdown = () => {
    if (!savedExplanation) return;

    const markdown = `# Flashcard Explanation\n\n## Question\n${card.term}\n\n## Answer\n${card.definition}\n\n## Explanation\n${savedExplanation}`;
    const blob = new Blob([markdown], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `flashcard-explanation-${Date.now()}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleRegenerate = async (e) => {
    e.stopPropagation();
    setSavedExplanation("");
    await sendMessage(generateExplanationPrompt());
  };

  const frontContent = (
    <div
      ref={frontRef}
      className="text-theme-0 font-sans font-medium leading-9"
      style={{ fontSize: `${frontFontSize}px` }}
    >
      {card.term}
    </div>
  );

  const explainButton = savedExplanation ? (
    <button
      onClick={handleExplain}
      className="text-theme-1 font-sans text-lg font-medium leading-none p-2"
    >
      See explanation
    </button>
  ) : (
    <button
      onClick={handleExplain}
      disabled={isStreaming}
      className="bg-theme-1 rounded-3xl text-theme-0 flex p-2 items-center hover:brightness-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <Lightbulb className="h-6" />
      <span className="px-1">Explain</span>
    </button>
  );

  const backToAnswerButton = (
    <button
      onClick={handleBackToAnswer}
      className="text-theme-1 font-sans text-lg font-medium leading-none p-2"
    >
      Back to answer
    </button>
  );

  const explanationActionBar = savedExplanation && !isStreaming && (
    <div className="flex gap-1 mt-2 -ml-1 text-theme-0">
      {/* <TooltipIconButton tooltip="Copy" onClick={handleCopy}>
        {isCopied ? (
          <Check className="h-4 w-4" />
        ) : (
          <Copy className="h-4 w-4" />
        )}
      </TooltipIconButton> */}
      {/* <TooltipIconButton
        tooltip="Export as Markdown"
        onClick={handleExportMarkdown}
      >
        <Download className="h-4 w-4" />
      </TooltipIconButton> */}
      <TooltipIconButton
        tooltip="Explain differently"
        onClick={handleRegenerate}
      >
        <RefreshCw className="h-4 w-4" />
      </TooltipIconButton>
    </div>
  );

  const backContent = (
    <div className="relative w-full h-full flex flex-col">
      <div
        ref={backRef}
        className={`text-theme-0 font-sans font-medium leading-9 pr-3 transition-opacity duration-300 absolute inset-0 ${
          showExplanation ? "opacity-0 pointer-events-none" : "opacity-100"
        }`}
        style={{ fontSize: `${backFontSize}px` }}
      >
        {card.definition}
      </div>
      <div
        className={`transition-opacity duration-300 flex flex-col ${
          showExplanation ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        <div
          className={`${
            isStreaming && !savedExplanation ? "animate-pulse" : ""
          } text-theme-0 font-sans font-normal text-sm leading-relaxed overflow-y-auto whitespace-pre-wrap pr-3`}
          style={{ maxHeight: `${EXPLANATION_MAX_HEIGHT}px` }}
        >
          <MarkdownRendering>
            {isStreaming
              ? "Generating explanation..."
              : savedExplanation
              ? savedExplanation
              : "Click 'Explain' to get an AI explanation..."}
          </MarkdownRendering>
        </div>
        {explanationActionBar}
      </div>
    </div>
  );

  return (
    <FlippableCard
      frontContent={frontContent}
      backContent={backContent}
      frontFlipButtonText="View answer"
      backFlipButtonText="Back to question"
      backActionButton={showExplanation ? backToAnswerButton : explainButton}
    />
  );
};
